import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createSupplierApi,
  deleteSupplierApi,
  getSupplierApi,
  getSuppliersApi,
  updateSupplierApi,
} from "../api/suppliersApi";
import {
  catalogQueryKey,
  type CatalogScope,
} from "../api/catalogScope";
import type { SupplierId } from "@KassaroAB/shared/types/ids";
import type {
  RestCreateSupplier,
  RestUpdateSupplier,
} from "@KassaroAB/shared/types/supplier";

const suppliersQueryKey = (scope: CatalogScope | null) =>
  catalogQueryKey(scope, "suppliers");

const invalidateSupplierQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  scope: CatalogScope | null,
) => {
  queryClient.invalidateQueries({ queryKey: suppliersQueryKey(scope) });
};

export const useGetSuppliersQuery = (scope: CatalogScope | null) => {
  return useQuery({
    queryKey: suppliersQueryKey(scope),
    queryFn: () => getSuppliersApi(scope!),
    enabled: !!scope,
  });
};

export const useGetSupplierQuery = (
  scope: CatalogScope | null,
  supplierId: SupplierId | null | undefined,
) => {
  return useQuery({
    queryKey: [...suppliersQueryKey(scope), supplierId],
    queryFn: () => getSupplierApi(scope!, supplierId!),
    enabled: !!scope && !!supplierId,
  });
};

export const useCreateSupplierMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (supplier: RestCreateSupplier) =>
      createSupplierApi(scope!, supplier),
    onSuccess: () => {
      invalidateSupplierQueries(queryClient, scope);
    },
  });
};

export const useUpdateSupplierMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      supplierId,
      supplier,
    }: {
      supplierId: SupplierId;
      supplier: RestUpdateSupplier;
    }) => updateSupplierApi(scope!, supplierId, supplier),
    onSuccess: () => {
      invalidateSupplierQueries(queryClient, scope);
    },
  });
};

export const useDeleteSupplierMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (supplierId: SupplierId) =>
      deleteSupplierApi(scope!, supplierId),
    onSuccess: () => {
      invalidateSupplierQueries(queryClient, scope);
    },
  });
};
